import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Typography, theme, Drawer, Space, Badge, Dropdown, Avatar, Alert } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  ProfileOutlined,
  RobotOutlined,
  CrownOutlined,
  StarFilled
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import NotificationCenter from '../realtime/NotificationCenter';
import PipelineQueueTray from '../../modules/ai-crm/components/PipelineQueueTray';
import OnboardingWizard from '../onboarding/OnboardingWizard';
import StickyChat from '../chat/StickyChat';
import { useAuthStore } from '../../stores/authStore';
import { useOnboarding } from '../../hooks/useOnboarding';
import { usePermissions } from '../../hooks/usePermissions';
import { useCurrentPlan } from '../../modules/billing/hooks/useCurrentPlan';
import LeadCreditsChip from '../../modules/billing/components/LeadCreditsChip';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(['ai-crm']); // AI CRM expanded by default
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { showOnboarding, completeOnboarding, isCompanyProfileComplete } = useOnboarding();
  const { hasPermission, canAccessModule } = usePermissions();
  const { data: currentPlan } = useCurrentPlan();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 處理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 用戶下拉菜單
  const userMenuItems = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: '個人資料',
      onClick: () => navigate('/settings/profile')
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '設定',
      onClick: () => navigate('/settings')
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '登出',
      onClick: handleLogout
    }
  ];

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  // Expand sidebar and open submenus when tour starts so spotlights can target menu items
  useEffect(() => {
    if (showOnboarding && !isMobile) {
      setCollapsed(false);
      setOpenKeys(['ai-crm', 'ai-crm/ai-assistant']);
    }
  }, [showOnboarding, isMobile]);

  // AI CRM Menu - Main menu items (Task 1: Only AI CRM and Super Admin visible)
  // Build AI Assistant submenu children based on permissions
  const aiAssistantChildren = [
    ...(hasPermission('ai_assistant:use') ? [{
      key: 'ai-crm/ai-assistant',
      label: 'Chat',
    }] : []),
    /* Email feature hidden - coming soon
    ...(canAccessModule('campaigns') ? [{
      key: 'ai-crm/email-campaigns',
      label: <span data-tour-id="email-campaigns">Email Campaign</span>,
    }] : []),
    */
    ...(canAccessModule('tasks') ? [{
      key: 'ai-crm/tasks',
      label: 'Tasks',
    }] : []),
  ];

  const menuItems = [
    // AI CRM Module - with reorganized menu structure (Task 4)
    {
      key: 'ai-crm',
      icon: <RobotOutlined />,
      label: 'AI CRM',
      children: [
        {
          key: 'ai-crm/overview',
          label: 'Dashboard',  // Renamed from "Overview"
        },
        ...(canAccessModule('lead_generation') ? [{
          key: 'ai-crm/lead-generation',
          label: <span data-tour-id="lead-generation">Lead Generation</span>,
        }] : []),
        {
          key: 'ai-crm/leads',
          label: <span data-tour-id="leads">Leads</span>,
        },
        {
          key: 'ai-crm/clients',
          label: 'Clients',
        },
        ...(aiAssistantChildren.length > 0 ? [{
          key: 'ai-crm/ai-assistant',
          label: 'AI Assistant',
          children: aiAssistantChildren,
        }] : []),
        {
          key: 'ai-crm/subscription',
          label: 'My Plan',
        },
      ],
    },
  ];

  // Add Super Admin menu if user has SUPER_ADMIN role
  const superAdminMenuItems = (user?.role === 'SUPER_ADMIN') ? [
    {
      key: 'admin',
      icon: <CrownOutlined />,
      label: 'Super Admin',
      children: [
        {
          key: 'admin/dashboard',
          label: 'Dashboard',
        },
        {
          key: 'admin/companies',
          label: 'Companies',
        },
        {
          key: 'admin/users',
          label: 'Users',
        },
        {
          key: 'admin/plans',
          label: 'Plans',
        },
        {
          key: 'admin/subscriptions',
          label: 'Subscriptions',
        },
        {
          key: 'admin/invoices',
          label: 'Invoices',
        },
        {
          key: 'admin/audit',
          label: 'Audit Logs',
        },
        {
          key: 'admin/user-activities',
          label: 'User Activities',
        },
        {
          key: 'admin/promotion-codes',
          label: 'Promotion Codes',
        },
      ],
    },
  ] : [];

  // Combine menu items (Settings at bottom — only for users with settings or team permissions)
  const canSeeSettings = hasPermission('settings:read') || hasPermission('settings:manage') || hasPermission('team:read');
  const settingsMenuItem = canSeeSettings ? [{
    key: 'settings',
    icon: <SettingOutlined />,
    label: <span data-tour-id="settings">Settings</span>,
    children: undefined as any,
  }] : [];
  const allMenuItems = [...menuItems, ...superAdminMenuItems, ...settingsMenuItem];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(`/${key}`);
    // Close mobile menu after navigation
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const getSelectedKeys = () => {
    const path = location.pathname.slice(1); // Remove leading slash
    if (path === '') return ['dashboard'];

    // Check if it's a nested route
    for (const item of allMenuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path === child.key || path.startsWith(child.key + '/')) {
            return [child.key];
          }
        }
      }
      if (path === item.key || path.startsWith(item.key + '/')) {
        return [item.key];
      }
    }

    return [path];
  };

  // Handle menu open/close - accordion behavior (only one submenu open at a time)
  const handleOpenChange = (keys: string[]) => {
    // Get root submenu keys (ai-crm, admin)
    const rootSubmenuKeys = allMenuItems.filter(item => item.children).map(item => item.key);

    // Find the latest opened key
    const latestOpenKey = keys.find(key => !openKeys.includes(key));

    if (latestOpenKey && rootSubmenuKeys.includes(latestOpenKey)) {
      // If opening a root submenu, close others and open only this one
      setOpenKeys([latestOpenKey]);
    } else {
      // For nested submenus or closing, just update normally
      setOpenKeys(keys);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname.slice(1);

    // Check nested routes first
    for (const item of allMenuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path === child.key || path.startsWith(child.key + '/')) {
            return child.label;
          }
        }
      }
      if (path === item.key) {
        return item.label;
      }
    }

    return 'Dashboard';
  };

  const renderSidebar = () => (
    <div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={getSelectedKeys()}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={allMenuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </div>
  );

  return (
    <>
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed} width={250}>
          {renderSidebar()}
        </Sider>
      )}
      
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title="Menu"
          placement="left"
          closable={true}
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0, backgroundColor: '#001529' }}
          width={250}
        >
          {renderSidebar()}
        </Drawer>
      )}

      <Layout style={{ flex: 1, minWidth: 0 }}>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 16px',
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: isMobile ? 56 : 64,
          lineHeight: 'normal',
          width: '100%',
          gap: isMobile ? 8 : 0,
        }}>
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => {
              if (isMobile) {
                setMobileMenuVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            style={{
              fontSize: '16px',
              width: isMobile ? 44 : 64,
              height: isMobile ? 44 : 64,
              flexShrink: 0,
            }}
          />

          {/* Title - hidden on mobile, spacer takes its place */}
          {!isMobile ? (
            <Space style={{ flex: 1, justifyContent: 'center' }}>
              <Title level={4} style={{
                margin: 0,
                color: '#011f4b',
                fontSize: '20px',
              }}>
                {getPageTitle()}
              </Title>
              <Badge
                count="Priority 5"
                style={{ backgroundColor: '#722ed1' }}
                title="Advanced Features Active"
              />
            </Space>
          ) : (
            <div style={{ flex: '1 1 auto' }} />
          )}

          {/* Right side actions */}
          <Space size={isMobile ? 4 : 8} style={{ flexShrink: 0 }}>
            <LeadCreditsChip compact={isMobile} />
            {!isMobile && (
              <Badge count="New" size="small" color="gold">
                <Button
                  type="text"
                  icon={<StarFilled style={{ color: '#faad14' }} />}
                  size="small"
                />
              </Badge>
            )}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space data-tour="user-menu" style={{ cursor: 'pointer', padding: isMobile ? '4px' : '8px' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#005b96' }}
                />
                {!isMobile && (
                  <span style={{ color: '#011f4b' }}>
                    {user?.name || user?.email || '用戶'}
                  </span>
                )}
              </Space>
            </Dropdown>
            <NotificationCenter companyId="demo_company" />
          </Space>
        </Header>
        <Content
          style={{
            margin: isMobile ? '0' : '24px 16px',
            padding: isMobile ? 0 : 24,
            background: isMobile ? 'transparent' : colorBgContainer,
            borderRadius: isMobile ? 0 : borderRadiusLG,
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          {!showOnboarding && !isCompanyProfileComplete && (
            <div style={{ marginBottom: isMobile ? 0 : 16, padding: isMobile ? '8px 12px' : 0 }}>
              <Alert
                message="Complete your company profile to help AI generate better results."
                type="info"
                showIcon
                closable
                banner
                action={
                  <Button size="small" type="link" onClick={() => navigate('/settings')}>
                    Go to Settings
                  </Button>
                }
              />
            </div>
          )}
          {/* Trial-expired hard banner — non-dismissable. Shows on every page
              until the user picks a paid plan. */}
          {currentPlan?.isTrialExpired && (
            <div style={{ marginBottom: isMobile ? 0 : 16, padding: isMobile ? '8px 12px' : 0 }}>
              <Alert
                message="Your trial has ended. Upgrade to keep using the platform."
                type="error"
                showIcon
                banner
                action={
                  <Button size="small" type="primary" danger onClick={() => navigate('/ai-crm/subscription')}>
                    Choose a plan
                  </Button>
                }
              />
            </div>
          )}
          <Outlet />
        </Content>
      </Layout>
      <OnboardingWizard
        open={showOnboarding}
        onComplete={completeOnboarding}
      />
    </Layout>
    {/* StickyChat is the AI Assistant widget — gated on CRM access (Business / Business Plus). */}
    {currentPlan?.crmEnabled && <StickyChat />}
    <PipelineQueueTray />
    </>
  );
};

export default MainLayout; 